/**
 * Jain Gaurav Yatra — Temples CRUD API.
 * Backend: /jain_gaurav_yatra/temples/  (cookie-session auth + X-Tenant-ID).
 * Create/update use multipart/form-data because `image` is an uploaded file.
 */
import { extractAdminTokens, getStoredAdminSession } from "@/lib/admin-login-api";

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "";
}

function tenantId(): string {
  return (import.meta.env.VITE_X_TENANTID as string | undefined) ?? "";
}

/** Tenant + optional Bearer. Never sets Content-Type so the browser can add the multipart boundary. */
function authHeaders(extra?: Record<string, string>): HeadersInit {
  const id = tenantId();
  const { access } = extractAdminTokens(getStoredAdminSession()?.session);
  return {
    ...(id ? { "X-Tenant-ID": id } : {}),
    ...(access ? { Authorization: `Bearer ${access}` } : {}),
    ...extra,
  };
}

function errMessage(data: unknown, status: number): string {
  const d = data as Record<string, unknown>;
  const pick = (v: unknown) => (typeof v === "string" ? v : null);
  const msg =
    pick(d?.message) ??
    pick(d?.detail) ??
    (Array.isArray(d?.non_field_errors) ? pick((d.non_field_errors as string[])[0]) : null);
  if (msg) {return msg;}
  try {
    return JSON.stringify(data);
  } catch {
    return `Request failed (${status})`;
  }
}

const TEMPLES_PATH = "/jain_gaurav_yatra/temples/";

/** Page-facing temple shape (matches the existing admin.temples UI fields). */
export interface Temple {
  id: string;
  yatra_order: number;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  mool_nayak: string;
  established_year: number | null;
  latitude: number | null;
  longitude: number | null;
  history_en: string;
  history_hi: string;
  history_gu: string;
  qr_code: string;
  /** Backend-generated QR code image URL (read-only). */
  qr_image: string | null;
  image_url: string | null;
  audio_url: string | null;
  created_at: string;
}

/** Fields accepted when creating/updating a temple. `imageFile`/`audioFile` are the optional uploads. */
export interface TempleWrite {
  yatra_order: number;
  name_en: string;
  name_hi: string;
  name_gu: string;
  area: string;
  mool_nayak: string;
  established_year: number | null;
  latitude: number | null;
  longitude: number | null;
  history_en: string;
  history_hi: string;
  history_gu: string;
  imageFile?: File | null;
  audioFile?: File | null;
}

function num(v: unknown): number | null {
  if (v === null || v === undefined || v === "") {return null;}
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string {
  return v === null || v === undefined ? "" : String(v);
}

/** Map an API row (snake_case english/hindi/gujarati) to the page shape. */
function toTemple(row: Record<string, unknown>): Temple {
  return {
    id: str(row.id),
    yatra_order: num(row.yatra_order) ?? 0,
    name_en: str(row.name_english),
    name_hi: str(row.name_hindi),
    name_gu: str(row.name_gujarati),
    area: str(row.area),
    mool_nayak: str(row.mool_nayak),
    established_year: num(row.established_year),
    latitude: num(row.latitude),
    longitude: num(row.longitude),
    history_en: str(row.history_english),
    history_hi: str(row.history_hindi),
    history_gu: str(row.history_gujarati),
    qr_code: str(row.qr_code),
    qr_image: row.qr_image ? str(row.qr_image) : null,
    image_url: row.image ? str(row.image) : null,
    audio_url: row.audio ? str(row.audio) : null,
    created_at: str(row.created_at),
  };
}

function toFormData(p: TempleWrite): FormData {
  const fd = new FormData();
  fd.append("yatra_order", String(p.yatra_order));
  fd.append("name_english", p.name_en);
  fd.append("name_hindi", p.name_hi);
  fd.append("name_gujarati", p.name_gu);
  fd.append("area", p.area);
  fd.append("mool_nayak", p.mool_nayak);
  if (p.established_year !== null) {fd.append("established_year", String(p.established_year));}
  if (p.latitude !== null) {fd.append("latitude", String(p.latitude));}
  if (p.longitude !== null) {fd.append("longitude", String(p.longitude));}
  fd.append("history_english", p.history_en);
  fd.append("history_hindi", p.history_hi);
  fd.append("history_gujarati", p.history_gu);
  if (p.imageFile) {fd.append("image", p.imageFile);}
  if (p.audioFile) {fd.append("audio", p.audioFile);}
  return fd;
}

async function parseJson(res: Response): Promise<unknown> {
  return res.json().catch(() => ({}));
}

export async function listTemples(): Promise<Temple[]> {
  const base = apiBase();
  if (!base) {throw new Error("API base URL not configured (VITE_API_BASE_URL).");}
  const res = await fetch(`${base}${TEMPLES_PATH}`, {
    method: "GET",
    headers: authHeaders(),
    credentials: "include",
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  const rows = Array.isArray(data)
    ? data
    : Array.isArray((data as { results?: unknown[] })?.results)
      ? (data as { results: unknown[] }).results
      : [];
  return (rows as Record<string, unknown>[]).map(toTemple);
}

export async function createTemple(payload: TempleWrite): Promise<Temple> {
  const base = apiBase();
  const res = await fetch(`${base}${TEMPLES_PATH}`, {
    method: "POST",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toTemple(data as Record<string, unknown>);
}

export async function updateTemple(id: string, payload: TempleWrite): Promise<Temple> {
  const base = apiBase();
  const res = await fetch(`${base}${TEMPLES_PATH}${id}/`, {
    method: "PATCH",
    headers: authHeaders(),
    credentials: "include",
    body: toFormData(payload),
  });
  const data = await parseJson(res);
  if (!res.ok) {throw new Error(errMessage(data, res.status));}
  return toTemple(data as Record<string, unknown>);
}

export async function deleteTemple(id: string): Promise<void> {
  const base = apiBase();
  const res = await fetch(`${base}${TEMPLES_PATH}${id}/`, {
    method: "DELETE",
    headers: authHeaders(),
    credentials: "include",
  });
  if (!res.ok && res.status !== 204) {
    const data = await parseJson(res);
    throw new Error(errMessage(data, res.status));
  }
}
